import React, { FC, ReactNode, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Collapse, Typography } from '@mui/material';
import { Button } from 'src/components/Button';
import {
  ButtonsContainer,
  Card,
  CardHeader,
  CollapseContent,
  Container,
  ExpandMore,
  IconsContainer,
  Loader,
  Number,
  ViewButton,
} from 'src/components/ConnectorCard/styled';
import { ConnectorType } from 'src/utils/gql/types';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useTrialStatus } from 'src/hooks/useTrialStatus';

interface ConnectorCardProps {
  type: ConnectorType;
  title: string;
  description: string;
  accountsNumber: number;
  icon: ReactNode;
  buttonLoading: boolean;
  loading: boolean;
  onConnect: (type: ConnectorType) => void;
  onConfigure: (type: ConnectorType) => void;
  onView: (type: ConnectorType) => void;
}

export const ConnectorCard: FC<ConnectorCardProps> = ({
  type,
  title,
  description,
  accountsNumber,
  icon,
  buttonLoading,
  loading,
  onConnect,
  onConfigure,
  onView,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { user } = useAccount();
  const snackbar = useSnackbar();
  const { isTrialExpired, shouldFreeze } = useTrialStatus();

  // Verifica se il Database Connector è disabilitato per l'utente
  const isDatabaseConnectorDisabled =
    type === ConnectorType.DATABASE && !user?.databaseConnectorEnabled;

  const isMagentoConnectorDisabled =
    type === ConnectorType.MAGENTO && !user?.magentoConnectorEnabled;

  // Verifica se Shopify è disabilitato
  // COMMENTATO: Abilitato Shopify per tutti gli utenti
  // const isShopifyConnectorDisabled = type === ConnectorType.SHOPIFY && !user?.shopifyConnectorEnabled;
  const isShopifyConnectorDisabled = false; // Shopify sempre abilitato

  // Verifica se l'utente è trial scaduto (blocca tutti i connettori)
  const isTrialExpiredDisabled = isTrialExpired || shouldFreeze;

  // Connettore disabilitato se trial scaduto O connettore specifico non abilitato
  const isConnectorDisabled =
    isTrialExpiredDisabled ||
    isDatabaseConnectorDisabled ||
    isMagentoConnectorDisabled ||
    isShopifyConnectorDisabled;

  const getDisabledMessage = () => {
    if (isTrialExpiredDisabled) {
      return 'Your trial has expired. Upgrade your plan to access connectors.';
    }
    if (isDatabaseConnectorDisabled) {
      return 'Database connector access not authorized. Contact the administrator to enable it.';
    }
    if (isMagentoConnectorDisabled) {
      return 'Magento connector access not authorized. Contact the administrator to enable it.';
    }
    if (isShopifyConnectorDisabled) {
      return 'Shopify connector access not authorized. Contact the administrator to enable it.';
    }
    return 'Connector access not authorized.';
  };

  const handleClick = () => {
    if (isConnectorDisabled) {
      snackbar(getDisabledMessage(), 'warning');
      return;
    }
    setExpanded(!expanded);
  };

  const handleConfigure = () => {
    if (isConnectorDisabled) {
      snackbar(getDisabledMessage(), 'warning');
      return;
    }
    onConfigure(type);
  };

  const handleView = () => {
    if (isConnectorDisabled) {
      snackbar(getDisabledMessage(), 'warning');
      return;
    }
    onView(type);
  };

  const handleConnect = () => {
    if (isConnectorDisabled) {
      snackbar(getDisabledMessage(), 'warning');
      return;
    }
    onConnect(type);
  };

  const handleConnectClick = () => {
    handleConnect();
  };

  const handleCardClick = () => {
    if (isConnectorDisabled) {
      snackbar(getDisabledMessage(), 'warning');
      return;
    }
    setExpanded(!expanded);
  };

  return (
    <Card
      style={{
        opacity: isConnectorDisabled ? 0.6 : 1,
        cursor: isConnectorDisabled ? 'not-allowed' : 'default',
      }}
      onClick={handleCardClick}
    >
      <Container>
        <CardHeader>
          <IconsContainer>
            {icon}
            {!!accountsNumber && <Number>{accountsNumber}</Number>}
          </IconsContainer>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2">{description}</Typography>
            {isConnectorDisabled && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {isTrialExpiredDisabled
                  ? 'Trial expired - Upgrade plan'
                  : 'Access not authorized'}
              </Typography>
            )}
          </Box>
        </CardHeader>
        {!!accountsNumber && (
          <ExpandMore expand={+expanded} onClick={handleClick}>
            <ArrowDropDownIcon />
          </ExpandMore>
        )}
      </Container>
      <Collapse in={expanded} timeout="auto">
        <CollapseContent>
          <ViewButton onClick={handleView}>
            <InfoIcon />
            <Typography>View metrics</Typography>
          </ViewButton>
        </CollapseContent>
      </Collapse>

      {loading && <Loader size={24} />}
      {!!accountsNumber && !loading && (
        <ButtonsContainer>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleConfigure}
            disabled={isConnectorDisabled}
          >
            Configure
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleConnectClick}
            loading={buttonLoading}
            disabled
          >
            Add accounts
          </Button>
        </ButtonsContainer>
      )}
      {!accountsNumber && !loading && (
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          onClick={handleConnect}
          loading={buttonLoading}
          disabled={isConnectorDisabled}
        >
          Connect
        </Button>
      )}
    </Card>
  );
};
